import { useState } from "react";
import { api } from "@/App";
import { DollarSign } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export function VehicleRentabilite({ vehicleId, coutRevient, montantFacture, onUpdate }) {
  const handleUpdate = async (field, value) => {
    try {
      await api.put(`/vehicles/${vehicleId}`, { [field]: value });
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const benefice = (montantFacture || 0) - (coutRevient || 0);

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Rentabilité
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cout_revient" className="text-sm text-slate-600">Coût de revient (€)</Label>
            <Input
              id="cout_revient"
              type="number"
              step="0.01"
              placeholder="0.00"
              defaultValue={coutRevient || ""}
              onBlur={(e) => handleUpdate("cout_revient", parseFloat(e.target.value) || 0)}
              className="mt-1"
            />
            <p className="text-xs text-slate-400 mt-1">Achats, pièces, sous-traitance...</p>
          </div>
          <div>
            <Label htmlFor="montant_facture" className="text-sm text-slate-600">Montant facturé (€)</Label>
            <Input
              id="montant_facture"
              type="number"
              step="0.01"
              placeholder="0.00"
              defaultValue={montantFacture || ""}
              onBlur={(e) => handleUpdate("montant_facture", parseFloat(e.target.value) || 0)}
              className="mt-1"
            />
          </div>
        </div>
        {(coutRevient || montantFacture) && (
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Bénéfice :</span>
              <span className={`text-lg font-bold ${benefice >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(benefice)}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function VehiclePriorite({ vehicleId, priorite, prioriteSemaine, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [weekInput, setWeekInput] = useState(prioriteSemaine || "");

  const handleToggle = async (checked) => {
    setLoading(true);
    try {
      await api.put(`/vehicles/${vehicleId}`, { priorite: checked, priorite_semaine: checked ? weekInput : "" });
      toast.success(checked ? "Marqué prioritaire" : "Priorité retirée");
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Erreur");
    }
    setLoading(false);
  };

  const handleWeekChange = async (val) => {
    setWeekInput(val);
    if (priorite) {
      try {
        await api.put(`/vehicles/${vehicleId}`, { priorite_semaine: val });
        if (onUpdate) onUpdate();
      } catch (e) {
        console.error("Échec mise à jour semaine de priorité:", e);
        toast.error("Impossible de mettre à jour la semaine");
      }
    }
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-4 h-4 rounded-full ${priorite ? 'bg-red-500' : 'bg-slate-300'}`} />
            <div>
              <p className="font-semibold text-slate-800">Dossier Prioritaire</p>
              <p className="text-sm text-slate-500">Gommette rouge dans Kanban et Planning</p>
            </div>
          </div>
          <Switch
            checked={priorite || false}
            onCheckedChange={handleToggle}
            disabled={loading}
          />
        </div>
        {priorite && (
          <div className="mt-3 flex items-center gap-2">
            <Label className="text-xs text-slate-600 whitespace-nowrap">A partir de la semaine du :</Label>
            <Input
              type="date"
              value={weekInput}
              onChange={(e) => handleWeekChange(e.target.value)}
              className="w-40 text-xs h-8"
              data-testid="priorite-semaine-input"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
